import { MICROSEGMENT_SERVICE_ID_TAG_KEY, MICROSEGMENT_SOURCE_TAG_KEY } from './microsegment-mapping.util';

/**
 * Assembles the resources a microsegment create left behind (one service plus its configs, dial/bind
 * policies, and - once they are tagged too - the granted identities) back into a single manageable unit.
 *
 * Pure and framework-agnostic: the caller fetches each resource type from the controller and hands the
 * raw rows in; this groups them. It is the first reader of the `nf-microsegment-service-id` tag that the
 * create pipeline stamps on every member (see `service-from-template.service.ts`).
 */

/** The shape read off any fetched row. Tags may sit at the top level or under `data`, depending on the
 *  resource type and the client that fetched it, so both are read. */
export interface MicrosegmentMemberRow {
    [key: string]: unknown;
    id?: string;
    name?: string;
    createdAt?: string;
    tags?: Record<string, unknown> | null;
    data?: { [key: string]: unknown; tags?: Record<string, unknown> | null; createdAt?: string } | null;
}

/** One assembled microsegment: the owning service and every resource stamped with its id. */
export interface Microsegment {
    /** The owning service's Ziti id - the value every member carries in its service-id tag. */
    serviceId: string;
    /** The microsegment's display name (the service's own name). */
    name: string;
    /** The service row. */
    service: MicrosegmentMemberRow;
    configs: MicrosegmentMemberRow[];
    policies: MicrosegmentMemberRow[];
    identities: MicrosegmentMemberRow[];
    /** When the service was created, if the row carried it (for a "Created" column). */
    createdAt?: string;
    /** Total resources in the segment: the service plus its configs, policies and identities. */
    memberCount: number;
}

/** The raw rows for each resource type, as fetched from the controller. */
export interface MicrosegmentSources {
    services?: MicrosegmentMemberRow[];
    configs?: MicrosegmentMemberRow[];
    policies?: MicrosegmentMemberRow[];
    identities?: MicrosegmentMemberRow[];
}

/** Read a row's tags whether they sit at the top level or under `data`. */
function tagsOf(row: MicrosegmentMemberRow | null | undefined): Record<string, unknown> {
    return (row?.tags ?? row?.data?.tags ?? {}) as Record<string, unknown>;
}

/** Read a tag value as a trimmed string, or '' when absent. */
function tagValue(row: MicrosegmentMemberRow, key: string): string {
    const raw = tagsOf(row)[key];
    return raw == null ? '' : `${raw}`.trim();
}

/**
 * Group the fetched rows into microsegments. Each service carrying the microsegment-source tag seeds a
 * segment keyed by its own id; every config, policy and identity is bucketed into the segment named by
 * its `nf-microsegment-service-id` tag. A member whose tag points to a service not in the set is dropped
 * (its service may be gone, or outside this page), so a segment never lists resources from another.
 */
export function assembleMicrosegments(sources: MicrosegmentSources): Microsegment[] {
    const segments = new Map<string, Microsegment>();

    for (const service of sources.services ?? []) {
        const id = `${service?.id ?? ''}`.trim();
        // Only services this workflow created (carrying the source tag) are microsegments; a plain or
        // template service is skipped even if some stray resource points at it.
        if (!id || !tagValue(service, MICROSEGMENT_SOURCE_TAG_KEY)) {
            continue;
        }
        segments.set(id, {
            serviceId: id,
            name: `${service?.name ?? service?.data?.['name'] ?? ''}`,
            service,
            configs: [],
            policies: [],
            identities: [],
            createdAt: service?.createdAt ?? service?.data?.createdAt,
            memberCount: 1,
        });
    }

    const bucket = (rows: MicrosegmentMemberRow[] | undefined, pick: (m: Microsegment) => MicrosegmentMemberRow[]) => {
        for (const row of rows ?? []) {
            const segment = segments.get(tagValue(row, MICROSEGMENT_SERVICE_ID_TAG_KEY));
            if (!segment) {
                continue;
            }
            pick(segment).push(row);
            segment.memberCount += 1;
        }
    };

    bucket(sources.configs, (m) => m.configs);
    bucket(sources.policies, (m) => m.policies);
    bucket(sources.identities, (m) => m.identities);

    return [...segments.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** The existing resource ids an in-place edit patches (mirrors UpdateExisting in service-from-template). */
export interface MicrosegmentExistingIds {
    serviceId: string;
    interceptConfigId?: string;
    hostConfigId?: string;
    dialPolicyId?: string;
    bindPolicyId?: string;
}

/** A microsegment mapped back into a "Create services from traffic" service card, so that page opens
 *  pre-filled for editing. The reverse of what the card's draft builder produces: raw values, `@name`/
 *  `#role` tokens, per-dimension host delivery (forward vs fixed), plus the ids to patch. */
export interface MicrosegmentCardSeed {
    name: string;
    /** Service role attributes as `#role` tokens. */
    attributes: string[];
    accessMode: 'address' | 'sdk';
    addresses: string[];
    /** Ports as individual chips ("22"); a true range maps to "low-high" (a Phase-1 limitation). */
    ports: string[];
    accessProtocol: 'tcp' | 'udp' | 'both';
    /** Who can dial, as `@name` / `#role` tokens. */
    dialTokens: string[];
    hostMode: 'address' | 'sdk';
    fwdProtocol: boolean;
    protocolVal: 'tcp' | 'udp';
    fwdAddress: boolean;
    addressVal: string;
    fwdPort: boolean;
    portVal: string;
    allowedAddresses: string[];
    allowedPorts: string[];
    allowedProtocols: string[];
    allowedSourceAddresses: string[];
    /** Who binds, as `@name` / `#role` tokens. */
    bindTokens: string[];
    existing: MicrosegmentExistingIds;
}

/** One port range as the card holds it: a single port is "22", a span is "8000-8010". */
function rangeToPortStrings(ranges: any[]): string[] {
    return (ranges ?? [])
        .map((r) => (r?.low === r?.high ? `${r?.low}` : `${r?.low}-${r?.high}`))
        .filter((s) => s && s !== 'undefined');
}

/** Map a microsegment back into a create-from-traffic service card for editing, including the ids to patch. */
export function microsegmentToTrafficCard(
    segment: Microsegment,
    identityNameById: Record<string, string> = {}
): MicrosegmentCardSeed {
    const configs = segment.configs ?? [];
    const interceptRow = configs.find((c) => {
        const b = body(c);
        return Array.isArray(b['addresses']) && b['portRanges'] != null;
    });
    const hostRow = configs.find((c) => c !== interceptRow);
    const intercept = body(interceptRow);
    const host = body(hostRow);

    const dialRow = segment.policies.find((p) => policyType(p) === 'dial');
    const bindRow = segment.policies.find((p) => policyType(p) === 'bind');
    const rolesOf = (row: MicrosegmentMemberRow | undefined) =>
        (((row as any)?.['identityRoles'] ?? (row?.data as any)?.identityRoles ?? []) as string[]).map((role) =>
            roleToToken(role, identityNameById)
        );

    const protocols = ((intercept['protocols'] ?? []) as string[]).map((p) => `${p}`.toLowerCase());
    const hasTcp = protocols.includes('tcp');
    const hasUdp = protocols.includes('udp');
    const accessProtocol: 'tcp' | 'udp' | 'both' = hasTcp && hasUdp ? 'both' : hasUdp ? 'udp' : 'tcp';

    const serviceBody = body(segment.service);

    return {
        name: segment.name,
        attributes: ((serviceBody['roleAttributes'] ?? []) as string[]).map((r) => (r.startsWith('#') ? r : `#${r}`)),
        accessMode: interceptRow ? 'address' : 'sdk',
        addresses: (intercept['addresses'] ?? []) as string[],
        ports: rangeToPortStrings(intercept['portRanges'] ?? []),
        accessProtocol,
        dialTokens: rolesOf(dialRow),
        hostMode: hostRow ? 'address' : 'sdk',
        fwdProtocol: !!host['forwardProtocol'],
        protocolVal: `${host['protocol'] ?? 'tcp'}` === 'udp' ? 'udp' : 'tcp',
        fwdAddress: !!host['forwardAddress'],
        addressVal: `${host['address'] ?? ''}`,
        fwdPort: !!host['forwardPort'],
        portVal: host['port'] != null ? `${host['port']}` : '',
        allowedAddresses: (host['allowedAddresses'] ?? []) as string[],
        allowedPorts: rangeToPortStrings(host['allowedPortRanges'] ?? []),
        allowedProtocols: (host['allowedProtocols'] ?? []) as string[],
        allowedSourceAddresses: (host['allowedSourceAddresses'] ?? []) as string[],
        bindTokens: rolesOf(bindRow),
        existing: {
            serviceId: segment.serviceId,
            interceptConfigId: interceptRow ? `${interceptRow.id ?? ''}` || undefined : undefined,
            hostConfigId: hostRow ? `${hostRow.id ?? ''}` || undefined : undefined,
            dialPolicyId: dialRow ? `${dialRow.id ?? ''}` || undefined : undefined,
            bindPolicyId: bindRow ? `${bindRow.id ?? ''}` || undefined : undefined,
        },
    };
}

/** A service-policy's Dial / Bind, lowercased, read whether at the top level or under `data`. */
function policyType(row: MicrosegmentMemberRow | undefined): string {
    return `${(row as any)?.['type'] ?? (row?.data as any)?.type ?? ''}`.toLowerCase();
}

/** Resolve one policy identity-role selector into a form attribute token: "@<id>" to "@<name>" (or "@<id>"
 *  when the name is unknown), "#role" as-is, a bare value to "#value" (the form treats bare as a role). */
function roleToToken(role: string, identityNameById: Record<string, string>): string {
    const r = `${role ?? ''}`.trim();
    if (!r) {
        return r;
    }
    if (r.startsWith('@')) {
        const id = r.slice(1);
        return `@${identityNameById[id] ?? id}`;
    }
    return r.startsWith('#') ? r : `#${r}`;
}

/** The display-ready surface facts a microsegment detail view shows: what it protects, how traffic is
 *  delivered, who can reach it, and its posture. Built from the member rows the segment already holds. */
export interface MicrosegmentSummary {
    /** Destination addresses clients dial (from the intercept config). */
    addresses: string[];
    /** Destination ports as individual tokens, each a single port or a range (e.g. ["22", "443", "8000-8010"]). */
    ports: string[];
    /** Destination ports, formatted (e.g. "22, 443", "8000-8010"). */
    portsLabel: string;
    /** Protocols the service carries (e.g. ["tcp", "udp"]). */
    protocols: string[];
    /** Where traffic is delivered on the host (e.g. "127.0.0.1:22"), or a forwarding description. */
    deliveryTarget: string;
    /** "Forward to host" when the host mirrors the dialed address/port, else "Fixed host". */
    deliveryMode: string;
    /** Delivery host type: an address host (a host.v1 config delivers to an address/port), or an
     *  SDK-hosted service (no host config - a bound SDK identity serves it). */
    hostType: 'address' | 'sdk';
    /** Whether the host mirrors (forwards) the dialed protocol / address / port instead of a fixed value. */
    forwardProtocol: boolean;
    forwardAddress: boolean;
    forwardPort: boolean;
    /** Identity names (or role selectors) allowed to dial (consume) the service. */
    dialIdentities: string[];
    /** Identity names (or role selectors) that bind (host) the service. */
    bindIdentities: string[];
    /** Whether end-to-end encryption is required on the service. */
    encryptionRequired: boolean;
    /** Whether it was created from the Traffic Analysis workflow. */
    fromTraffic: boolean;
    createdAt?: string;
    counts: { configs: number; policies: number; identities: number };
}

function body(row: MicrosegmentMemberRow | undefined): Record<string, any> {
    return ((row?.data as any)?.data ?? (row?.data as any) ?? row ?? {}) as Record<string, any>;
}

/** Format a port range list: a single port as "22", a range as "8000-8010", joined by commas. */
function formatPortRanges(ranges: any[]): string {
    return (ranges ?? [])
        .map((r) => (r?.low === r?.high ? `${r?.low}` : `${r?.low}-${r?.high}`))
        .filter((s) => s && s !== 'undefined')
        .join(', ');
}

/** Resolve one policy identity-role selector to a display string: "@<id>" to the identity's name (or the
 *  id when unknown), "#<attr>" to the attribute as written, anything else as-is. */
function resolveRole(role: string, identityNameById: Record<string, string>): string {
    const r = `${role ?? ''}`.trim();
    if (r.startsWith('@')) {
        const id = r.slice(1);
        return identityNameById[id] ?? id;
    }
    return r;
}

/** Summarize a microsegment's surface facts for the detail view. Pure: pass an id->name map (from the
 *  identities already fetched) to resolve the policy identity roles to names. */
export function summarizeMicrosegment(
    segment: Microsegment,
    identityNameById: Record<string, string> = {}
): MicrosegmentSummary {
    // The intercept config governs addresses + ports; the host config describes delivery. Identify by
    // body shape (intercept carries `addresses` + `portRanges`), not by name, which the user can change.
    const configs = segment.configs ?? [];
    const interceptRow = configs.find((c) => {
        const b = body(c);
        return Array.isArray(b['addresses']) && b['portRanges'] != null;
    });
    const hostRow = configs.find((c) => c !== interceptRow);
    const intercept = body(interceptRow);
    const host = body(hostRow);

    const hostAddr = host['address'] ?? (host['forwardAddress'] ? '' : '');
    const hostPort = host['port'] ?? '';
    const forwards = !!host['forwardAddress'] && !!host['forwardPort'];
    const deliveryTarget = hostAddr && hostPort ? `${hostAddr}:${hostPort}` : forwards ? 'Same as destination' : [hostAddr || 'forwarded', hostPort || 'forwarded'].join(':');

    const roleNames = (type: string) =>
        segment.policies
            .filter((p) => `${p?.['type'] ?? (p?.data as any)?.type ?? ''}`.toLowerCase() === type)
            .flatMap((p) => (((p as any)['identityRoles'] ?? (p?.data as any)?.identityRoles ?? []) as string[]))
            .map((role) => resolveRole(role, identityNameById));

    const serviceBody = body(segment.service);

    return {
        addresses: (intercept['addresses'] ?? []) as string[],
        ports: rangeToPortStrings(intercept['portRanges'] ?? []),
        portsLabel: formatPortRanges(intercept['portRanges'] ?? []),
        protocols: (intercept['protocols'] ?? []) as string[],
        deliveryTarget,
        deliveryMode: forwards ? 'Forward to host' : 'Fixed host',
        hostType: hostRow ? 'address' : 'sdk',
        forwardProtocol: !!host['forwardProtocol'],
        forwardAddress: !!host['forwardAddress'],
        forwardPort: !!host['forwardPort'],
        dialIdentities: roleNames('dial'),
        bindIdentities: roleNames('bind'),
        encryptionRequired: serviceBody['encryptionRequired'] !== false,
        fromTraffic: tagsOf(segment.service)[MICROSEGMENT_SOURCE_TAG_KEY] === 'traffic-analysis',
        createdAt: segment.createdAt,
        counts: { configs: segment.configs.length, policies: segment.policies.length, identities: segment.identities.length },
    };
}
